import { createElement, type ComponentProps, type ComponentType, type MouseEventHandler } from 'react';
import { type TFunction } from '@pkgs/components';
import { type Currency, type Language, MarketCountryCode } from '@pkgs/api';
import { type currencies, type Units } from '@ev-tech/ui-core-components/utils';
import { type SettingsModal } from '@ev-tech/ui-core-components/SettingsModal';
import { type NavigationLink } from '@ev-tech/ui-core-components/Navigation';
import { type NavigationItem } from '@ev-tech/ui-core-components/MetaNavigation';
import { type StoryblokHeaderProps } from '@ev-tech/ui-core-components/Header';
import { type SbBaseBlockProps, type StoryblokDatasourceEntry } from '@ev-tech/ui-core-components/cms';

import { type NavigationTrackingContext } from '@/types/tracking';
import { Pin } from '@/icons/small/Pin';
import { Globe } from '@/icons/small/Globe';

import type { mapLanguageAndName } from './mapLanguageName';
import { getLinkUrl, linkResolver } from './linkResolver';
import imageResolver from './imageResolver';
import { ELEMENT_IDS } from '../constants';

type HeaderBlock = SbBaseBlockProps<StoryblokHeaderProps>['blok'];
type HeaderItem = HeaderBlock['items'][number];
type HeaderCategory = NonNullable<HeaderItem['categories_list']>[number];
type SettingsModalProps = ComponentProps<typeof SettingsModal>;

type TrackItem = (navigationData: NavigationTrackingContext) => void;

const buildNavigationListItem = ({
  listItem,
  itemIndex,
  categoryIndex,
  listItemIndex,
  externalLinkDomains,
  trackItem,
}: {
  listItem: HeaderCategory['list'][number];
  itemIndex: number;
  categoryIndex: number;
  listItemIndex: number;
  externalLinkDomains: StoryblokDatasourceEntry[];
  trackItem: TrackItem;
}) => ({
  id: listItem._uid,
  onClick: () =>
    trackItem({
      elementId: `${ELEMENT_IDS.menuHeader.title}-${itemIndex + 1}_${ELEMENT_IDS.menuHeader.column}-${categoryIndex + 1}_${ELEMENT_IDS.menuHeader.link}-${listItemIndex + 1}`,
      href: getLinkUrl(listItem.href),
    }),
  ...linkResolver({ linkObject: listItem.href, label: listItem.label, externalLinkDomains }),
});

const buildFlyoutCategory = ({
  category,
  itemIndex,
  categoryIndex,
  externalLinkDomains,
  trackItem,
}: {
  category: HeaderCategory;
  itemIndex: number;
  categoryIndex: number;
  externalLinkDomains: StoryblokDatasourceEntry[];
  trackItem: TrackItem;
}) => ({
  id: category._uid,
  overline: category.overline,
  list: category.list.map((listItem, listItemIndex) =>
    buildNavigationListItem({
      listItem,
      itemIndex,
      categoryIndex,
      listItemIndex,
      externalLinkDomains,
      trackItem,
    }),
  ),
});

export const buildMetaNavigation = ({
  blok,
  countryCode,
  language,
  currency,
  unit,
  settingsLabels,
  settingsModalState,
  countryModalState,
  parsedCountries,
  languages,
  currentCountryName,
  locationsLabel,
  unitsArray,
  settingsModalComponent,
  onMouseEnter,
  onMouseLeave,
  onSettingsClick,
  onNavigationClose,
  onLanguageChange,
  onCurrencyChange,
  onUnitChange,
  onCountryChange,
  onCountryModalClose,
  onApplyButton,
  trackItem,
}: {
  blok: HeaderBlock;
  countryCode: MarketCountryCode;
  language: Language;
  currency: Currency;
  unit: Units;
  settingsLabels: SettingsModalProps['labels'];
  settingsModalState: SettingsModalProps['overlayState'];
  countryModalState: SettingsModalProps['countryModalState'];
  parsedCountries: SettingsModalProps['countries'];
  languages: ReturnType<typeof mapLanguageAndName>;
  currentCountryName: string;
  locationsLabel: string;
  unitsArray: SettingsModalProps['units'];
  settingsModalComponent: ComponentType<SettingsModalProps>;
  onMouseEnter: MouseEventHandler;
  onMouseLeave: MouseEventHandler;
  onSettingsClick: MouseEventHandler;
  onNavigationClose: SettingsModalProps['onNavigationClose'];
  onLanguageChange: SettingsModalProps['onLanguageChange'];
  onCurrencyChange: SettingsModalProps['onCurrencyChange'];
  onUnitChange: SettingsModalProps['onUnitChange'];
  onCountryChange: SettingsModalProps['onCountryChange'];
  onCountryModalClose: SettingsModalProps['onCountryModalClose'];
  onApplyButton: SettingsModalProps['onApplyButton'];
  trackItem: TrackItem;
}): NavigationItem[] => [
  {
    id: '1',
    icon: createElement(Globe, { 'data-testid': 'header-settings-icon' } as never),
    label: language.toUpperCase(),
    onMouseEnter,
    onMouseLeave,
    onClick: onSettingsClick,
    children: parsedCountries
      ? createElement(settingsModalComponent, {
          currentLanguage: language,
          hideCountryContainer: Boolean(blok.privateOffice),
          tenant: countryCode.toLocaleLowerCase(),
          settings: {
            currency: currency as keyof typeof currencies,
            unit,
            language,
            country: countryCode,
          },
          labels: settingsLabels,
          overlayState: settingsModalState,
          onNavigationClose,
          onLanguageChange,
          onCurrencyChange,
          onUnitChange,
          countryModalState,
          countries: parsedCountries,
          onCountryChange,
          onCountryModalClose,
          onShimClose: onCountryModalClose,
          onCloseSettings: onCountryModalClose,
          onApplyButton,
          languages,
          units: unitsArray,
          currentCountryName,
        } as SettingsModalProps)
      : undefined,
    as: 'button',
  },
  ...(countryCode === MarketCountryCode['private-office']
    ? []
    : [
        {
          id: '2',
          icon: createElement(Pin),
          href: getLinkUrl(blok.locationsLink),
          label: locationsLabel,
          hideTextOnDesktop: false,
          onClick: () =>
            trackItem({
              elementId: `${ELEMENT_IDS.metaHeader.title}_${ELEMENT_IDS.metaHeader.locations}`,
              href: getLinkUrl(blok.locationsLink),
            }),
          as: 'a' as const,
        },
      ]),
];

export const buildNavigation = ({
  blok,
  externalLinkDomains,
  t,
  tCommon,
  trackItem,
  onTeaserLinkClick,
}: {
  blok: HeaderBlock;
  externalLinkDomains: StoryblokDatasourceEntry[] | null | undefined;
  t: (key: 'header.navigation.title' | 'header.navigation.button.back' | 'header.navigation.button.close') => string;
  tCommon: TFunction;
  trackItem: TrackItem;
  onTeaserLinkClick: (itemIndex: number, item: HeaderItem) => void | undefined;
}): NavigationLink[] =>
  blok.items.map((item, itemIndex) => ({
    ...linkResolver({ linkObject: item.href, label: item.label, externalLinkDomains: externalLinkDomains ?? [] }),
    id: item._uid,
    onClick: () =>
      trackItem({
        href: getLinkUrl(item.href),
        elementId: `${ELEMENT_IDS.menuHeader.title}-${itemIndex + 1}`,
      }),
    flyout: item.categories_list?.length
      ? {
          id: item._uid,
          title: t('header.navigation.title'),
          backButtonLabel: t('header.navigation.button.back'),
          closeButtonLabel: t('header.navigation.button.close'),
          content: {
            id: `${item._uid}${item.content_headline}`,
            intro: {
              headline: item.content_headline,
              copy: item.content_copy,
            },
            nav: {
              categories: item.categories_list.map((category, categoryIndex) =>
                buildFlyoutCategory({
                  category,
                  itemIndex,
                  categoryIndex,
                  externalLinkDomains: externalLinkDomains ?? [],
                  trackItem,
                }),
              ),
            },
            headerTeaser:
              item.categories_list.length < 3 && item.teaserElement?.[0]
                ? {
                    overline: item.teaserElement[0].overline,
                    headline: item.teaserElement[0].headline,
                    image: imageResolver({
                      image: item.teaserElement[0].image,
                      t: tCommon,
                      options: {
                        width: '668',
                        height: '501',
                      },
                    }),
                    link: {
                      ...linkResolver({
                        linkObject: item.teaserElement[0].link,
                        label: item.teaserElement[0].link_label,
                        externalLinkDomains: externalLinkDomains ?? [],
                      }),
                      onClick: () => onTeaserLinkClick(itemIndex, item),
                    },
                    horizontal: item.categories_list.length === 1,
                  }
                : undefined,
          },
        }
      : undefined,
  }));
